import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Button,
  Toggle,
  InputNumber,
  Loader,
  Message,
  useToaster,
  Panel,
  IconButton,
  Input,
  TimePicker,
  SelectPicker,
  Divider,
  Heading,
  Form
} from 'rsuite';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/config/hooks';
import { getBuildingById, updateBuilding } from '@/services/floor.service';
import { getCrons, manageCron, deleteCron } from '@/services/cron.service';
import CheckIcon from '@rsuite/icons/Check';
import PlusIcon from '@rsuite/icons/Plus';
import TrashIcon from '@rsuite/icons/Trash';
import { v4 as uuidv4 } from 'uuid';

interface SettingsModalProps {
  open: boolean;
  handleClose: () => void;
  buildingId: string | null;
}

interface Cron {
  id: string;
  type: 'notification' | 'cancellation';
  resource: 'parking' | 'desk';
  schedule: {
    minute: string;
    hour: string;
    dayOfMonth: string;
    month: string;
    dayOfWeek: string;
  };
  fieldTitle: string;
  message: string;
  enabled: boolean;
}

interface Building {
  _id?: string;
  checkinLocation?: boolean;
  latitude?: number;
  longitude?: number;
  radius?: number;
  reassignment?: boolean;
}

const resourceOptions = [
  { label: 'Parking', value: 'parking' },
  { label: 'Desk', value: 'desk' }
];

const SettingsModal: React.FC<SettingsModalProps> = ({ open, handleClose, buildingId }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const toaster = useToaster();

  const building = useAppSelector((state: any) => state.building?.item);
  const itemLoading = useAppSelector((state: any) => state.building?.itemLoading);
  const updateLoading = useAppSelector((state: any) => state.building?.updateLoading);
  const updateError = useAppSelector((state: any) => state.building?.updateError);
  const cronState = useAppSelector((state: any) => state.cron);

  const [geolocationEnabled, setGeolocationEnabled] = useState(false);
  const [buildingLatitude, setBuildingLatitude] = useState<number | undefined>();
  const [buildingLongitude, setBuildingLongitude] = useState<number | undefined>();
  const [radius, setRadius] = useState<number | undefined>();
  const [reassignmentEnabled, setReassignmentEnabled] = useState(false);
  const [notifications, setNotifications] = useState<Cron[]>([]);
  const [initialNotifications, setInitialNotifications] = useState<Cron[]>([]);
  const [cancellation, setCancellation] = useState<Cron | null>(null);
  const [initialCancellation, setInitialCancellation] = useState<Cron | null>(null);
  const [updateAttempted, setUpdateAttempted] = useState(false);
  const [openPanels, setOpenPanels] = useState({
    geolocation: true,
    reassignment: true,
    notification: new Map<string, boolean>(),
    cancellation: false
  });
  const [expandedPanels, setExpandedPanels] = useState({
    geolocation: true,
    reassignment: true,
    notification: new Map<string, boolean>(),
    cancellation: true
  });

  useEffect(() => {
    if (buildingId && open) {
      dispatch(getBuildingById(buildingId));
      dispatch(getCrons())
        .then(data => {
          console.log('Raw data from getCrons:', JSON.stringify(data, null, 2));
          const filteredCrons = Array.isArray(data)
            ? data.filter(
                item =>
                  item.buildingId?.toString() === buildingId &&
                  (item.type === 'notification' || item.type === 'cancellation')
              )
            : [];
          const notificationCrons = filteredCrons
            .filter(cron => cron.type === 'notification')
            .map(cron => ({
              id: cron._id?.toString() || uuidv4(),
              type: 'notification' as 'notification',
              resource: cron.resource || 'desk',
              schedule: {
                minute: cron.schedule?.minute || '*',
                hour: cron.schedule?.hour || '*',
                dayOfMonth: cron.schedule?.dayOfMonth || '*',
                month: cron.schedule?.month || '*',
                dayOfWeek: cron.schedule?.dayOfWeek || '*'
              },
              fieldTitle: cron.fieldTitle || '',
              message: cron.message || '',
              enabled: cron.enabled !== undefined ? cron.enabled : true
            }));
          const cancellationCron = filteredCrons.find(cron => cron.type === 'cancellation');
          const mappedCancellation = cancellationCron
            ? {
                id: cancellationCron._id?.toString() || uuidv4(),
                type: 'cancellation' as 'cancellation',
                resource: cancellationCron.resource || 'desk',
                schedule: {
                  minute: cancellationCron.schedule?.minute || '*',
                  hour: cancellationCron.schedule?.hour || '*',
                  dayOfMonth: cancellationCron.schedule?.dayOfMonth || '*',
                  month: cancellationCron.schedule?.month || '*',
                  dayOfWeek: cancellationCron.schedule?.dayOfWeek || '*'
                },
                fieldTitle: cancellationCron.fieldTitle || '',
                message: cancellationCron.message || '',
                enabled: cancellationCron.enabled !== undefined ? cancellationCron.enabled : true
              }
            : null;
          setNotifications(notificationCrons);
          setInitialNotifications(JSON.parse(JSON.stringify(notificationCrons)));
          setCancellation(mappedCancellation);
          setInitialCancellation(
            mappedCancellation ? JSON.parse(JSON.stringify(mappedCancellation)) : null
          );
          const notificationOpen = new Map<string, boolean>();
          const notificationExpanded = new Map<string, boolean>();
          notificationCrons.forEach(cron => {
            notificationOpen.set(cron.id, true);
            notificationExpanded.set(cron.id, true);
          });
          setOpenPanels(prev => ({
            ...prev,
            notification: notificationOpen,
            cancellation: !!mappedCancellation
          }));
          setExpandedPanels(prev => ({
            ...prev,
            notification: notificationExpanded,
            cancellation: !!mappedCancellation
          }));
          console.log(
            `Fetched crons for building ${buildingId}:`,
            JSON.stringify(filteredCrons, null, 2)
          );
        })
        .catch(err => {
          console.error('Error fetching crons:', err.message);
          toaster.push(
            <Message type="error" duration={5000} showIcon>
              {t(
                err.message.includes('404')
                  ? 'Cron endpoint not found. Please check the server configuration.'
                  : err.message || 'Failed to load cron jobs'
              )}
            </Message>,
            { placement: 'topCenter' }
          );
          setNotifications([]);
          setInitialNotifications([]);
          setCancellation(null);
          setInitialCancellation(null);
        });
    } else {
      setNotifications([]);
      setInitialNotifications([]);
      setCancellation(null);
      setInitialCancellation(null);
      setOpenPanels({
        geolocation: true,
        reassignment: true,
        notification: new Map(),
        cancellation: false
      });
      setExpandedPanels({
        geolocation: true,
        reassignment: true,
        notification: new Map(),
        cancellation: true
      });
    }
  }, [buildingId, open, t, toaster, dispatch]);

  useEffect(() => {
    if (building) {
      setGeolocationEnabled(building.checkinLocation || false);
      setBuildingLatitude(building.latitude);
      setBuildingLongitude(building.longitude);
      setRadius(building.radius);
      setReassignmentEnabled(building.reassignment || false);
    }
  }, [building]);

  useEffect(() => {
    if (updateAttempted && !updateLoading && !cronState.isLoading) {
      if (updateError || cronState.error) {
        toaster.push(
          <Message type="error" duration={5000} showIcon>
            {t(cronState.error || updateError || 'Error updating settings')}
          </Message>,
          { placement: 'topCenter' }
        );
      } else if (buildingId) {
        toaster.push(
          <Message type="success" duration={5000} showIcon>
            {t('Settings Updated Successfully')}{' '}
          </Message>,
          { placement: 'topCenter' }
        );
        handleClose();
      }
      setUpdateAttempted(false);
    }
  }, [updateLoading, updateError, cronState, updateAttempted, buildingId, handleClose, t, toaster]);

  const hasBuildingChanges = () => {
    return (
      building?.checkinLocation !== geolocationEnabled ||
      (geolocationEnabled &&
        (building?.latitude !== buildingLatitude ||
          building?.longitude !== buildingLongitude ||
          building?.radius !== radius)) ||
      building?.reassignment !== reassignmentEnabled
    );
  };

  const getChangedCrons = () => {
    const changedCrons: Cron[] = [];
    notifications.forEach(cron => {
      const initialCron = initialNotifications.find(n => n.id === cron.id);
      if (!initialCron || JSON.stringify(cron) !== JSON.stringify(initialCron)) {
        changedCrons.push(cron);
      }
    });
    if (openPanels.cancellation && cancellation) {
      if (
        !initialCancellation ||
        JSON.stringify(cancellation) !== JSON.stringify(initialCancellation)
      ) {
        changedCrons.push(cancellation);
      }
    }
    return changedCrons;
  };

  const handleSave = async () => {
    if (
      geolocationEnabled &&
      (buildingLatitude === undefined || buildingLongitude === undefined || radius === undefined)
    ) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t('All geolocation fields are required when geolocation is enabled')}
        </Message>,
        { placement: 'topCenter' }
      );
      return;
    }

    if (notifications.some(n => !n.fieldTitle || !n.message || !n.resource)) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t('All notification crons must have a resource, title, and message')}
        </Message>,
        { placement: 'topCenter' }
      );
      return;
    }

    if (
      cancellation &&
      openPanels.cancellation &&
      (!cancellation.fieldTitle || !cancellation.message || !cancellation.resource)
    ) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t('Cancellation cron must have a resource, title, and message')}
        </Message>,
        { placement: 'topCenter' }
      );
      return;
    }

    try {
      if (buildingId) {
        if (hasBuildingChanges()) {
          const updateData: Partial<Building> = {
            checkinLocation: geolocationEnabled,
            latitude: geolocationEnabled ? buildingLatitude : undefined,
            longitude: geolocationEnabled ? buildingLongitude : undefined,
            radius: geolocationEnabled ? radius : undefined,
            reassignment: reassignmentEnabled
          };
          await dispatch(updateBuilding(buildingId, updateData));
        }

        const deletedNotifications = initialNotifications.filter(
          init => !notifications.some(curr => curr.id === init.id)
        );
        for (const deleted of deletedNotifications) {
          if (!deleted.id.includes('-')) {
            console.log(`Deleting notification cron: ${deleted.id}`);
            await dispatch(deleteCron(deleted.id));
          }
        }

        if (
          !openPanels.cancellation &&
          initialCancellation &&
          !initialCancellation.id.includes('-')
        ) {
          console.log(`Deleting cancellation cron: ${initialCancellation.id}`);
          await dispatch(deleteCron(initialCancellation.id));
        }

        const changedCrons = getChangedCrons();
        for (const cron of changedCrons) {
          const cronData = {
            _id: cron.id.includes('-') ? undefined : cron.id,
            type: cron.type,
            resource: cron.resource,
            schedule: {
              minute: cron.schedule.minute,
              hour: cron.schedule.hour,
              dayOfMonth: cron.schedule.dayOfMonth,
              month: cron.schedule.month,
              dayOfWeek: cron.schedule.dayOfWeek
            },
            fieldTitle: cron.fieldTitle,
            message: cron.message,
            enabled: cron.enabled,
            buildingId
          };
          console.log(`Saving cron: ${JSON.stringify(cronData)}`);
          await dispatch(manageCron(cronData));
        }
      }
      setInitialNotifications(JSON.parse(JSON.stringify(notifications)));
      setInitialCancellation(
        openPanels.cancellation && cancellation ? JSON.parse(JSON.stringify(cancellation)) : null
      );
      setUpdateAttempted(true);
    } catch (err) {
      console.error('Error saving settings:', err.message);
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t(err.message || 'Error saving settings')}
        </Message>,
        { placement: 'topCenter' }
      );
      setUpdateAttempted(false);
    }
  };

  const togglePanel = (panel: keyof typeof openPanels | string, id?: string) => {
    if (panel === 'notification' && id) {
      setOpenPanels(prev => {
        const newNotification = new Map(prev.notification);
        if (newNotification.has(id)) {
          newNotification.delete(id);
          setNotifications(prev => prev.filter(n => n.id !== id));
        }
        return { ...prev, notification: newNotification };
      });
    } else if (panel === 'cancellation') {
      setOpenPanels(prev => ({
        ...prev,
        cancellation: !prev.cancellation
      }));
      setCancellation(null);
    }
  };

  const toggleExpanded = (panel: keyof typeof expandedPanels | string, id?: string) => {
    if (panel === 'notification' && id) {
      setExpandedPanels(prev => {
        const newNotification = new Map(prev.notification);
        newNotification.set(id, !newNotification.get(id));
        return { ...prev, notification: newNotification };
      });
    } else {
      setExpandedPanels(prev => ({
        ...prev,
        [panel]: !prev[panel as keyof typeof expandedPanels]
      }));
    }
  };

  const addNotification = () => {
    const newId = uuidv4();
    setNotifications(prev => [
      ...prev,
      {
        id: newId,
        type: 'notification' as 'notification',
        resource: 'desk',
        schedule: { minute: '*', hour: '*', dayOfMonth: '*', month: '*', dayOfWeek: '*' },
        fieldTitle: '',
        message: '',
        enabled: true
      }
    ]);
    setOpenPanels(prev => ({
      ...prev,
      notification: new Map(prev.notification).set(newId, true)
    }));
    setExpandedPanels(prev => ({
      ...prev,
      notification: new Map(prev.notification).set(newId, true)
    }));
  };

  const updateCron = (
    id: string,
    field: 'resource' | 'schedule.minute' | 'schedule.hour' | 'fieldTitle' | 'message' | 'enabled',
    value: string | boolean
  ) => {
    setNotifications(prev =>
      prev.map(n => {
        if (n.id === id) {
          if (field.startsWith('schedule.')) {
            const scheduleField = field.split('.')[1] as 'minute' | 'hour';
            return { ...n, schedule: { ...n.schedule, [scheduleField]: value as string } };
          }
          return { ...n, [field]: value };
        }
        return n;
      })
    );
  };

  const updateCancellation = (
    field: 'resource' | 'schedule.minute' | 'schedule.hour' | 'fieldTitle' | 'message' | 'enabled',
    value: string | boolean
  ) => {
    setCancellation(prev =>
      prev
        ? {
            ...prev,
            [field.startsWith('schedule.') ? 'schedule' : field]: field.startsWith('schedule.')
              ? { ...prev.schedule, [field.split('.')[1]]: value }
              : value
          }
        : {
            id: uuidv4(),
            type: 'cancellation' as 'cancellation',
            resource: field === 'resource' ? (value as 'parking' | 'desk') : 'desk',
            schedule: {
              minute: field === 'schedule.minute' ? (value as string) : '*',
              hour: field === 'schedule.hour' ? (value as string) : '*',
              dayOfMonth: '*',
              month: '*',
              dayOfWeek: '*'
            },
            fieldTitle: field === 'fieldTitle' ? (value as string) : '',
            message: field === 'message' ? (value as string) : '',
            enabled: field === 'enabled' ? (value as boolean) : true
          }
    );
  };

  return (
    <Drawer open={open} onClose={handleClose} size="sm">
      <Drawer.Header>
        <Drawer.Title>{t('Settings')}</Drawer.Title>
        <Drawer.Actions>
          {updateLoading ? (
            <Loader content={t('Saving...')} />
          ) : (
            <IconButton
              loading={updateLoading || cronState.isLoading}
              onClick={handleSave}
              appearance="primary"
              icon={<CheckIcon />}
              disabled={updateLoading || cronState.isLoading}
            >
              {t('Save')}
            </IconButton>
          )}
          <Button onClick={handleClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>
      <Drawer.Body>
        {itemLoading || cronState.isLoading ? (
          <Loader content={t('Loading settings...')} />
        ) : (
          <>
            {/* <Panel
              header={t('Check geolocation for scan')}
              collapsible
              expanded={expandedPanels.geolocation}
              onSelect={() => toggleExpanded('geolocation')}
              style={{ marginBottom: 10 }}
            > */}
            <Divider style={{ marginBottom: 50 }}>
              <Heading level={6}>{t('Check geolocation for scan')}</Heading>
            </Divider>
            <div style={{ marginBottom: 20 }}>
              <label style={{ marginRight: '10px' }}>{t('Geolocation')}</label>
              <Toggle
                checked={geolocationEnabled}
                onChange={setGeolocationEnabled}
                checkedChildren={t('Enabled')}
                unCheckedChildren={t('Disabled')}
              />
            </div>
            {geolocationEnabled && (
              <>
                <div style={{ marginBottom: 20 }}>
                  <label>{t('Building latitude')}</label>
                  <InputNumber
                    value={buildingLatitude}
                    onChange={(value: string | number | undefined) => {
                      if (value === '' || value === undefined) {
                        setBuildingLatitude(undefined);
                      } else {
                        const parsed = parseFloat(value as string);
                        setBuildingLatitude(isNaN(parsed) ? undefined : parsed);
                      }
                    }}
                    min={-90}
                    max={90}
                    step={0.001}
                    placeholder={t('Enter latitude')}
                  />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label>{t('Building longitude')}</label>
                  <InputNumber
                    value={buildingLongitude}
                    onChange={(value: string | number | undefined) => {
                      if (value === '' || value === undefined) {
                        setBuildingLongitude(undefined);
                      } else {
                        const parsed = parseFloat(value as string);
                        setBuildingLongitude(isNaN(parsed) ? undefined : parsed);
                      }
                    }}
                    min={-180}
                    max={180}
                    step={0.001}
                    placeholder={t('Enter longitude')}
                  />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label>{t('Radius')}</label>
                  <InputNumber
                    value={radius}
                    onChange={(value: string | number | undefined) => {
                      if (value === '' || value === undefined) {
                        setRadius(undefined);
                      } else {
                        const parsed = parseFloat(value as string);
                        setRadius(isNaN(parsed) ? undefined : parsed);
                      }
                    }}
                    min={0}
                    step={1}
                    placeholder={t('Enter radius')}
                    postfix={t('Meter')}
                  />
                </div>
              </>
            )}
            {/* </Panel> */}
            {/* <Panel
              header={t('Reassignment Settings For Reservations')}
              collapsible
              expanded={expandedPanels.reassignment}
              onSelect={() => toggleExpanded('reassignment')}
              style={{ marginBottom: 10 }}
            > */}
            <Divider style={{ marginBottom: 50, marginTop: 50 }}>
              <Heading level={6}>{t('Reassignment Of Reservations')}</Heading>
            </Divider>
            <div style={{ marginBottom: 20 }}>
              <Form fluid>
                <Form.Group>
                  <label style={{ marginRight: '10px' }}>{t('Reassignment Of Reservations')}</label>
                  <Form.HelpText tooltip>
                    {t(
                      'If this option is enabled, requests will be automatically set to validated upon submission. If disabled, requests will follow the standard approval process and require manual validation.'
                    )}
                  </Form.HelpText>
                </Form.Group>
              </Form>
              <Toggle
                checked={reassignmentEnabled}
                onChange={setReassignmentEnabled}
                checkedChildren={t('Enabled')}
                unCheckedChildren={t('Disabled')}
              />
            </div>
            {/* </Panel> */}

            <Divider style={{ marginBottom: 50, marginTop: 50 }}>
              <Heading level={6}>{t('Notification settings')}</Heading>
            </Divider>
            <div style={{ marginBottom: 10 }}>
              <IconButton
                icon={<PlusIcon />}
                onClick={addNotification}
                appearance="subtle"
                size="sm"
              >
                {t('Add Notification Settings')}
              </IconButton>
            </div>
            {notifications.map(
              (notification, index) =>
                openPanels.notification.get(notification.id) && (
                  <Panel
                    key={notification.id}
                    header={`${t('Notification')} ${index + 1}`}
                    collapsible
                    expanded={expandedPanels.notification.get(notification.id)}
                    onSelect={() => toggleExpanded('notification', notification.id)}
                    style={{ marginBottom: 10 }}
                  >
                    <div style={{ marginBottom: 20 }}>
                      <label>{t('Resource')}</label>
                      <SelectPicker
                        data={resourceOptions}
                        value={notification.resource}
                        onChange={(value: 'parking' | 'desk' | null) =>
                          value && updateCron(notification.id, 'resource', value)
                        }
                        placeholder={t('Select resource')}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <label>{t('Notification Send Time')}</label>
                      <TimePicker
                        format="HH:mm"
                        placement="auto"
                        value={
                          notification.schedule.hour !== '*' && notification.schedule.minute !== '*'
                            ? new Date(
                                0,
                                0,
                                0,
                                Number(notification.schedule.hour),
                                Number(notification.schedule.minute)
                              )
                            : null
                        }
                        onChange={(value: Date | null) => {
                          updateCron(
                            notification.id,
                            'schedule.hour',
                            value ? value.getHours().toString() : '*'
                          );
                          updateCron(
                            notification.id,
                            'schedule.minute',
                            value ? value.getMinutes().toString() : '*'
                          );
                        }}
                        placeholder={t('Select notification send time')}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <label>{t('Field Title')}</label>
                      <Input
                        value={notification.fieldTitle}
                        onChange={(value: string) =>
                          updateCron(notification.id, 'fieldTitle', value)
                        }
                        placeholder={t('Enter field title')}
                      />
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <label>{t('Message')}</label>
                      <Input
                        value={notification.message}
                        onChange={(value: string) => updateCron(notification.id, 'message', value)}
                        placeholder={t('Enter message')}
                      />
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <Toggle
                        checked={notification.enabled}
                        onChange={(value: boolean) => updateCron(notification.id, 'enabled', value)}
                        checkedChildren={t('Enabled')}
                        unCheckedChildren={t('Disabled')}
                      />
                    </div>
                    <IconButton
                      icon={<TrashIcon />}
                      onClick={() => togglePanel('notification', notification.id)}
                      appearance="subtle"
                      size="sm"
                      style={{ marginTop: 10 }}
                    >
                      {t('Delete Notification Settings')}
                    </IconButton>
                  </Panel>
                )
            )}
            <Divider>
              <Heading level={6}>{t('Cancellation settings')}</Heading>
            </Divider>
            {!openPanels.cancellation && (
              <div style={{ marginBottom: 10 }}>
                <IconButton
                  icon={<PlusIcon />}
                  onClick={() => {
                    setCancellation({
                      id: uuidv4(),
                      type: 'cancellation',
                      resource: 'desk',
                      schedule: {
                        minute: '*',
                        hour: '*',
                        dayOfMonth: '*',
                        month: '*',
                        dayOfWeek: '*'
                      },
                      fieldTitle: '',
                      message: '',
                      enabled: true
                    });
                    setOpenPanels(prev => ({ ...prev, cancellation: true }));
                    setExpandedPanels(prev => ({ ...prev, cancellation: true }));
                  }}
                  appearance="subtle"
                  size="sm"
                >
                  {t('Add Cancellation Settings')}
                </IconButton>
              </div>
            )}
            {openPanels.cancellation && cancellation && (
              <Panel
                header={t('Automatic Cancellation')}
                collapsible
                expanded={expandedPanels.cancellation}
                onSelect={() => toggleExpanded('cancellation')}
                style={{ marginBottom: 10 }}
              >
                <div style={{ marginBottom: 20 }}>
                  <label>{t('Resource')}</label>
                  <SelectPicker
                    data={resourceOptions}
                    value={cancellation.resource}
                    onChange={(value: 'parking' | 'desk' | null) =>
                      value && updateCancellation('resource', value)
                    }
                    placeholder={t('Select resource')}
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label>{t('Cancellation Time')}</label>
                  <TimePicker
                    format="HH:mm"
                    placement="auto"
                    value={
                      cancellation.schedule.hour !== '*' && cancellation.schedule.minute !== '*'
                        ? new Date(
                            0,
                            0,
                            0,
                            Number(cancellation.schedule.hour),
                            Number(cancellation.schedule.minute)
                          )
                        : null
                    }
                    onChange={(value: Date | null) => {
                      updateCancellation(
                        'schedule.hour',
                        value ? value.getHours().toString() : '*'
                      );
                      updateCancellation(
                        'schedule.minute',
                        value ? value.getMinutes().toString() : '*'
                      );
                    }}
                    placeholder={t('Select cancellation time')}
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label>{t('Field Title')}</label>
                  <Input
                    value={cancellation.fieldTitle}
                    onChange={(value: string) => updateCancellation('fieldTitle', value)}
                    placeholder={t('Enter field title')}
                  />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label>{t('Message')}</label>
                  <Input
                    value={cancellation.message}
                    onChange={(value: string) => updateCancellation('message', value)}
                    placeholder={t('Enter message')}
                  />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <Toggle
                    checked={cancellation.enabled}
                    onChange={(value: boolean) => updateCancellation('enabled', value)}
                    checkedChildren={t('Enabled')}
                    unCheckedChildren={t('Disabled')}
                  />
                </div>
                <IconButton
                  icon={<TrashIcon />}
                  onClick={() => togglePanel('cancellation')}
                  appearance="subtle"
                  size="sm"
                  style={{ marginTop: 10 }}
                >
                  {t('Delete Cancellation Settings')}
                </IconButton>
              </Panel>
            )}
            {(updateError || cronState.error) && updateAttempted && (
              <Message type="error" showIcon style={{ marginTop: 10 }}>
                {t(cronState.error || updateError || 'Error updating settings')}
              </Message>
            )}
          </>
        )}
      </Drawer.Body>
    </Drawer>
  );
};

export default SettingsModal;
